import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { Constants } from 'src/app/app.constants';
import { MerchantService } from '../../../services/index';
import { PendingReward } from '../../../models/index';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';

@Component({
  selector: 'pending-rewards-dialog',
  templateUrl: './pending-rewards-dialog.component.html',
  styleUrls: ['./pending-rewards-dialog.component.css'],
})
export class PendingRewardsDialogComponent implements OnInit {
  pendingRewards: PendingReward[] = [];
  loading = false;
  error = false;
  pageSize = 50;
  pageIndex = 0;
  length = 0;
  range = '';
  suppressPaging = false;
  scrollTimeout: any;

  constructor(
    public matDialogRef: MatDialogRef<PendingRewardsDialogComponent>,
    private merchantService: MerchantService,
    private dialog: MatDialog,
    public constants: Constants,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.getPendingRewards();
  }

  get emptyString(): string {
    return 'No pending rewards to display';
  }

  getPendingRewards(): void {
    this.loading = true;
    this.merchantService
      .getPendingRewards(this.pageSize, this.pageIndex)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.length = res.totalDocs;
            this.pendingRewards = res.docs;

            this.range = this.constants.pageCounter(
              this.pageIndex,
              this.pageSize,
              this.length,
              this.pendingRewards?.length
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  pageEvent(event: any) {
    if (!this.suppressPaging) {
      this.pageIndex = event.offset;
      this.getPendingRewards();
    }
  }

  revertPendingReward(pendingReward: PendingReward): void {
    const giftedByText = pendingReward.merchantInitiated
      ? 'merchant-initiated'
      : `gifted by ${pendingReward.giftedByUser?.name || 'unknown user'}`;

    const confirmMessage = `Are you sure you want to revert this pending reward?<br><br>
      <b>Reward:</b> ${pendingReward.reward?.title}<br>
      <b>Email:</b> ${pendingReward.email}<br>
      <b>Type:</b> ${giftedByText}<br><br>
      This action cannot be undone.`;

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirm Revert',
        body: confirmMessage,
        buttonOne: 'Cancel',
        buttonTwo: 'Revert',
      },
      autoFocus: false,
      width: '400px',
      disableClose: true,
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((option) => {
      if (option == 1) {
        this.performRevert(pendingReward);
      }
    });
  }

  private performRevert(pendingReward: PendingReward): void {
    this.loading = true;
    this.merchantService
      .revertPendingReward(pendingReward._id!)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack('Pending reward successfully reverted');
            this.getPendingRewards(); // Refresh the list
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    return (
      this.datePipe.transform(
        new Date(date),
        this.constants.shortDate,
        'en-GB'
      ) || ''
    );
  }

  getGiftedByText(pendingReward: PendingReward): string {
    if (pendingReward.merchantInitiated) {
      return 'Merchant Initiated';
    }
    return pendingReward.giftedByUser?.name || 'Unknown User';
  }

  handleScroll() {
    this.suppressPaging = true;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.suppressPaging = false;
    }, 100);
  }
}
